import * as Sentry from "@sentry/nextjs";
import { getCmsJourneyPlannerStation } from "../../../app/api/getCmsJourneyPlannerStation";
import { BaseAction } from "../../../core/actions/BaseAction";
import { filterCmsStationsByStops } from "../../../Lib/Utils/stationGrouping";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse, ICmsNetworkMapStations, ICmsStationResponse, StationsListType } from "../../../Orchestrator/Types/Index";
import { stopAction } from "../Search/GetStopsAction";

/**
 * How long the station list waits on GET /stops before shipping unfiltered.
 * Long enough for a cold call that also has to mint an anonymous token, short
 * enough that a hang is a blink rather than a blank page.
 */
const STOPS_WAIT_MS = 5000;

/**
 * Stations the retailer stops API knows about, used to keep the shuttle stops
 * and partner venues in the CMS `stations` collection out of the journey
 * planner (see filterCmsStationsByStops).
 *
 * Reuses `stopsResponseAtom` when it is already filled — the landing page loads
 * stops on every visit, and re-fetching them here would put a second GET /stops
 * on the wire for a list that has not changed. Same shape as
 * footerRouteSearch.fireRouteSearchByStationId, which asks the same question.
 *
 * Reads the answer back off the ATOM rather than `stopAction.result`: the action
 * is a singleton that does not deduplicate, and BaseAction nulls `_result` at
 * the start of every run, so a concurrent execute() elsewhere in the app would
 * hand us a null mid-await. The atom is written by the run that succeeded and
 * survives that reset.
 *
 * Never throws, and never waits forever: a station list is not worth failing or
 * stalling a page over, and the caller treats an empty list as "could not tell",
 * not as "no stations".
 */
async function getStationCodes(): Promise<StationsListType> {
  const cached = getRecoilStateValue<StationsListType>("stopsResponseAtom");
  if (Array.isArray(cached) && cached.length > 0) return cached;

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const loadStops = async (): Promise<StationsListType> => {
      await stopAction.execute();
      return getRecoilStateValue<StationsListType>("stopsResponseAtom") ?? [];
    };

    // ApiRequest has no request timeout, so a hung GET /stops would otherwise
    // hold the whole station list back — on a page that needed no API of its own
    // before this filter existed. Giving up hands the fail-open path an empty
    // list, which shows the unfiltered CMS entries and reports itself, rather
    // than leaving the planner blank for as long as the socket stays open.
    return await Promise.race([
      loadStops(),
      new Promise<StationsListType>((resolve) => {
        timer = setTimeout(() => resolve([]), STOPS_WAIT_MS);
      }),
    ]);
  } catch (error) {
    // Unreachable today — BaseAction absorbs everything run() throws and
    // getRecoilStateValue is a synchronous read — so this guards a future
    // execute() that rejects, not a failure mode we have seen.
    console.error("Error fetching stops for the journey planner:", error);
    return [];
  } finally {
    clearTimeout(timer);
  }
}

class GetCmsJourneyPlannerStationAction extends BaseAction<ICmsNetworkMapStations[], void> {
  override async run(): Promise<ICmsNetworkMapStations[] | null> {
    let listOfData: ICmsNetworkMapStations[] = [];
    try {
      // Both reads are independent; the stops call must not delay the CMS one.
      const [stops, response] = await Promise.all([
        getStationCodes(),
        getCmsJourneyPlannerStation(),
      ]);
      const cmsResponse: IApiResponse<ICmsStationResponse> = response;
      if (cmsResponse.status === "ok" && cmsResponse.answer) {
        const data = cmsResponse.answer.data;
        if (data.length > 0) {
          // Filtered HERE rather than in the dropdown so the detail page agrees:
          // it resolves its slug against this same atom, and a filter that lived
          // in the <Select> would still render a full page for
          // /journey-planner/shuttle-fujairah.
          const { stations, failedOpen } = filterCmsStationsByStops(data, stops);
          listOfData = stations;

          // Failing open restores the very bug this filter exists to fix —
          // shuttle stops and partner venues back in the planner — so it must not
          // pass silently. Warning, not error: the page still works.
          if (failedOpen) {
            Sentry.captureMessage("journey planner: station list left unfiltered", {
              level: "warning",
              tags: { action: "GetCmsJourneyPlannerStationAction" },
              extra: { cmsEntries: data.length, stopsLoaded: stops.length },
            });
          }
        } else {
          console.warn("No data available.");
        }
      } else {
        console.warn("Failed to fetch data.");
      }
    } catch (error: any) {
      console.error("Error fetching data:", error);
    } finally {
      updateRecoilState("journeyPlannerStationAtom", listOfData);
      return listOfData;
    }
  }
}

export const getCmsJourneyPlannerStationAction = new GetCmsJourneyPlannerStationAction()
